/**
 * CoachPlaybook - Quiz Player Component
 * Version: 0.6.0
 * Description: Interactive quiz UI — tap, multiple choice, and draw modes with court visualization
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  generateQuiz,
  evaluateTapAnswer,
  evaluateChoiceAnswer,
  evaluateDrawAnswer,
  getQuizProgress
} from '../../utils/learn/quizEngine';

const QuizPlayer = ({ play, sport, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnPoints, setDrawnPoints] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);
  const canvasRef = useRef(null);
  const courtRef = useRef(null);

  useEffect(() => {
    if (play) {
      const generated = generateQuiz(play);
      setQuestions(generated);
      setCurrentIndex(0);
      setAnswers([]);
      setFeedback(null);
      setShowAnswer(false);
      setQuizComplete(false);
    }
  }, [play]);

  const currentQuestion = questions[currentIndex] || null;
  const progress = getQuizProgress(answers);

  const advanceQuestion = useCallback(() => {
    setFeedback(null);
    setShowAnswer(false);
    setDrawnPoints([]);

    if (currentIndex + 1 >= questions.length) {
      setQuizComplete(true);
      if (onComplete) {
        onComplete(getQuizProgress([...answers]));
      }
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, questions.length, answers, onComplete]);

  const recordAnswer = useCallback((correct) => {
    setAnswers(prev => [...prev, { questionId: currentQuestion?.id, correct }]);
  }, [currentQuestion]);

  const handleTap = useCallback((positionId) => {
    if (feedback) return;
    const result = evaluateTapAnswer(currentQuestion, positionId);
    setFeedback(result);
    recordAnswer(result.correct);
  }, [currentQuestion, feedback, recordAnswer]);

  const handleChoice = useCallback((optionId) => {
    if (feedback) return;
    const result = evaluateChoiceAnswer(currentQuestion, optionId);
    setFeedback(result);
    recordAnswer(result.correct);
  }, [currentQuestion, feedback, recordAnswer]);

  const handleDrawSubmit = useCallback(() => {
    if (feedback) return;
    const result = evaluateDrawAnswer(currentQuestion, drawnPoints);
    setFeedback({ ...result, correct: result.correct });
    recordAnswer(result.correct);
  }, [currentQuestion, drawnPoints, feedback, recordAnswer]);

  // Drawing handlers
  const getCourtCoords = useCallback((e) => {
    const rect = courtRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100
    };
  }, []);

  const onDrawStart = useCallback((e) => {
    if (currentQuestion?.type !== 'draw' || feedback) return;
    e.preventDefault();
    const pt = getCourtCoords(e);
    if (pt) {
      setIsDrawing(true);
      setDrawnPoints([pt]);
    }
  }, [currentQuestion, feedback, getCourtCoords]);

  const onDrawMove = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pt = getCourtCoords(e);
    if (pt) {
      setDrawnPoints(prev => [...prev, pt]);
    }
  }, [isDrawing, getCourtCoords]);

  const onDrawEnd = useCallback(() => {
    setIsDrawing(false);
  }, []);

  // Styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#0f172a',
      color: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    progressBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      backgroundColor: '#1e293b',
      borderBottom: '1px solid #334155'
    },
    progressTrack: {
      flex: 1,
      height: '8px',
      backgroundColor: '#334155',
      borderRadius: '4px',
      overflow: 'hidden'
    },
    progressFill: {
      height: '100%',
      backgroundColor: '#3b82f6',
      borderRadius: '4px',
      transition: 'width 0.3s ease'
    },
    progressText: {
      fontSize: '14px',
      color: '#94a3b8',
      fontWeight: '600',
      minWidth: '60px',
      textAlign: 'right'
    },
    prompt: {
      padding: '16px 20px',
      fontSize: '18px',
      fontWeight: '700',
      color: '#f1f5f9',
      textAlign: 'center',
      backgroundColor: '#1e293b',
      borderBottom: '1px solid #334155'
    },
    court: {
      position: 'relative',
      flex: 1,
      margin: '16px',
      backgroundColor: '#1a1a2e',
      borderRadius: '16px',
      border: '2px solid #334155',
      overflow: 'hidden',
      touchAction: 'none',
      cursor: currentQuestion?.type === 'draw' ? 'crosshair' : 'default'
    },
    player: (pos, isCorrect, isWrong, isTappable) => ({
      position: 'absolute',
      left: `${pos.startX}%`,
      top: `${pos.startY}%`,
      transform: 'translate(-50%, -50%)',
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      backgroundColor: isCorrect ? '#22c55e' : isWrong ? '#ef4444' : (pos.color || '#3b82f6'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      fontWeight: '800',
      color: '#fff',
      cursor: isTappable ? 'pointer' : 'default',
      border: isCorrect ? '3px solid #86efac' : isWrong ? '3px solid #fca5a5' : '2px solid rgba(255,255,255,0.3)',
      boxShadow: isCorrect
        ? '0 0 20px rgba(34,197,94,0.5)'
        : isWrong
          ? '0 0 20px rgba(239,68,68,0.5)'
          : '0 2px 8px rgba(0,0,0,0.3)',
      transition: 'all 0.3s ease',
      zIndex: 10,
      animation: isWrong ? 'shake 0.5s ease' : isCorrect ? 'pulse 0.5s ease' : 'none'
    }),
    choiceGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '10px',
      padding: '16px 20px'
    },
    choiceButton: (isSelected, isCorrect, isWrong) => ({
      padding: '14px 16px',
      borderRadius: '12px',
      border: '2px solid',
      borderColor: isCorrect ? '#22c55e' : isWrong ? '#ef4444' : '#475569',
      backgroundColor: isCorrect ? 'rgba(34,197,94,0.15)' : isWrong ? 'rgba(239,68,68,0.15)' : '#1e293b',
      color: isCorrect ? '#86efac' : isWrong ? '#fca5a5' : '#e2e8f0',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.2s ease'
    }),
    feedbackBar: (correct) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 20px',
      backgroundColor: correct ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
      borderTop: `2px solid ${correct ? '#22c55e' : '#ef4444'}`,
      color: correct ? '#86efac' : '#fca5a5',
      fontSize: '15px',
      fontWeight: '600'
    }),
    nextButton: {
      padding: '10px 24px',
      borderRadius: '10px',
      backgroundColor: '#3b82f6',
      color: '#fff',
      border: 'none',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer'
    },
    drawControls: {
      display: 'flex',
      gap: '10px',
      padding: '12px 20px',
      justifyContent: 'center'
    },
    drawButton: (variant) => ({
      padding: '10px 20px',
      borderRadius: '10px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      backgroundColor: variant === 'primary' ? '#3b82f6' : variant === 'danger' ? '#ef4444' : '#475569',
      color: '#fff'
    }),
    completionCard: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: '40px 20px',
      textAlign: 'center'
    },
    scoreCircle: (accuracy) => ({
      width: '140px',
      height: '140px',
      borderRadius: '50%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '24px',
      backgroundColor: accuracy >= 80 ? 'rgba(34,197,94,0.2)' : accuracy >= 50 ? 'rgba(251,191,36,0.2)' : 'rgba(239,68,68,0.2)',
      border: `4px solid ${accuracy >= 80 ? '#22c55e' : accuracy >= 50 ? '#fbbf24' : '#ef4444'}`
    }),
    hintButton: {
      padding: '8px 16px',
      borderRadius: '8px',
      border: '1px solid #475569',
      backgroundColor: 'transparent',
      color: '#94a3b8',
      fontSize: '13px',
      cursor: 'pointer',
      marginTop: '4px'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: '40px',
      color: '#94a3b8',
      textAlign: 'center'
    }
  };

  // No play or no questions
  if (!play) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>📋</p>
          <p style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0' }}>No Play Selected</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>Select a play from the library to start a quiz.</p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>🤔</p>
          <p style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0' }}>No Questions Generated</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>This play needs more detail (steps, paths, or passes) to generate quiz questions.</p>
        </div>
      </div>
    );
  }

  // Quiz complete screen
  if (quizComplete) {
    const finalProgress = getQuizProgress(answers);
    return (
      <div style={styles.container}>
        <div style={styles.completionCard}>
          <div style={styles.scoreCircle(finalProgress.accuracy)}>
            <span style={{ fontSize: '42px', fontWeight: '900', color: '#f1f5f9' }}>{finalProgress.accuracy}%</span>
            <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '600' }}>Accuracy</span>
          </div>
          <h2 style={{ fontSize: '28px', fontWeight: '900', marginBottom: '8px' }}>Quiz Complete!</h2>
          <p style={{ fontSize: '16px', color: '#94a3b8', marginBottom: '24px' }}>
            {play.name || 'Unnamed Play'}
          </p>
          <div style={{ display: 'flex', gap: '24px', marginBottom: '32px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#22c55e' }}>{finalProgress.correct}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Correct</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#ef4444' }}>{finalProgress.total - finalProgress.correct}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Incorrect</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '800', color: '#fbbf24' }}>{finalProgress.streakBest}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Best Streak</div>
            </div>
          </div>
          <button
            onClick={() => {
              setCurrentIndex(0);
              setAnswers([]);
              setFeedback(null);
              setShowAnswer(false);
              setQuizComplete(false);
              const regenerated = generateQuiz(play);
              setQuestions(regenerated);
            }}
            style={styles.nextButton}
          >
            Retry Quiz
          </button>
        </div>
      </div>
    );
  }

  const positions = play.positions || [];

  return (
    <div style={styles.container}>
      {/* Inline keyframes */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translate(-50%, -50%) translateX(0); }
          25% { transform: translate(-50%, -50%) translateX(-6px); }
          75% { transform: translate(-50%, -50%) translateX(6px); }
        }
        @keyframes pulse {
          0% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.15); }
          100% { transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>

      {/* Progress Bar */}
      <div style={styles.progressBar}>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${((currentIndex + 1) / questions.length) * 100}%` }} />
        </div>
        <span style={styles.progressText}>{currentIndex + 1} / {questions.length}</span>
      </div>

      {/* Question Prompt */}
      {currentQuestion && (
        <div style={styles.prompt}>{currentQuestion.prompt}</div>
      )}

      {/* Court Area */}
      <div
        ref={courtRef}
        style={styles.court}
        onMouseDown={onDrawStart}
        onMouseMove={onDrawMove}
        onMouseUp={onDrawEnd}
        onMouseLeave={onDrawEnd}
        onTouchStart={onDrawStart}
        onTouchMove={onDrawMove}
        onTouchEnd={onDrawEnd}
      >
        {/* Court markings (simplified) */}
        <svg
          viewBox="0 0 100 100"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.15 }}
          preserveAspectRatio="none"
        >
          <rect x="0" y="0" width="100" height="100" fill="none" stroke="#94a3b8" strokeWidth="0.5" />
          <circle cx="50" cy="50" r="15" fill="none" stroke="#94a3b8" strokeWidth="0.3" />
          <line x1="0" y1="50" x2="100" y2="50" stroke="#94a3b8" strokeWidth="0.3" />
        </svg>

        {/* Correct path overlay (shown after answer) */}
        {showAnswer && currentQuestion?.type === 'draw' && currentQuestion.correctAnswer && (
          <svg
            viewBox="0 0 100 100"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 5 }}
            preserveAspectRatio="none"
          >
            <polyline
              points={currentQuestion.correctAnswer.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="#22c55e"
              strokeWidth="1"
              strokeDasharray="2,1"
              opacity="0.8"
            />
          </svg>
        )}

        {/* User drawn path */}
        {drawnPoints.length > 1 && (
          <svg
            viewBox="0 0 100 100"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 6 }}
            preserveAspectRatio="none"
          >
            <polyline
              points={drawnPoints.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="#f59e0b"
              strokeWidth="0.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}

        {/* Player positions */}
        {positions.map(pos => {
          const isTappable = currentQuestion?.type === 'tap' && !feedback;
          const isCorrectTarget = feedback && pos.id === (feedback.correctPositionId || currentQuestion?.correctAnswer);
          const isWrongTap = feedback && !feedback.correct && pos.id !== currentQuestion?.correctAnswer && feedback.correctPositionId !== pos.id;

          return (
            <div
              key={pos.id}
              style={styles.player(pos, isCorrectTarget, false, isTappable)}
              onClick={() => isTappable && handleTap(pos.id)}
            >
              {pos.positionLabel}
            </div>
          );
        })}
      </div>

      {/* Choice options (for MC questions) */}
      {currentQuestion?.type === 'choice' && currentQuestion.options && (
        <div style={styles.choiceGrid}>
          {currentQuestion.options.map(option => {
            const isCorrect = feedback && option.correct;
            const isWrong = feedback && !option.correct && option.id === feedback.correctOptionId;
            return (
              <button
                key={option.id}
                style={styles.choiceButton(false, isCorrect, isWrong && !feedback.correct)}
                onClick={() => handleChoice(option.id)}
                disabled={!!feedback}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Draw controls */}
      {currentQuestion?.type === 'draw' && !feedback && (
        <div style={styles.drawControls}>
          <button
            style={styles.drawButton('secondary')}
            onClick={() => setDrawnPoints([])}
          >
            Clear
          </button>
          <button
            style={styles.drawButton('primary')}
            onClick={handleDrawSubmit}
            disabled={drawnPoints.length < 2}
          >
            Submit Drawing
          </button>
        </div>
      )}

      {/* Draw score display */}
      {currentQuestion?.type === 'draw' && feedback && (
        <div style={{ textAlign: 'center', padding: '8px', color: '#94a3b8', fontSize: '14px' }}>
          Path accuracy: <strong style={{ color: '#f1f5f9' }}>{feedback.score || 0}%</strong>
        </div>
      )}

      {/* Hint / Show Answer button */}
      {!feedback && currentQuestion?.hints?.length > 0 && (
        <div style={{ textAlign: 'center', padding: '4px' }}>
          <button style={styles.hintButton} onClick={() => setShowAnswer(true)}>
            {showAnswer ? currentQuestion.hints[0] : 'Show Hint'}
          </button>
        </div>
      )}

      {/* Feedback bar */}
      {feedback && (
        <div style={styles.feedbackBar(feedback.correct)}>
          <span>{feedback.feedback}</span>
          <button style={styles.nextButton} onClick={advanceQuestion}>
            {currentIndex + 1 >= questions.length ? 'See Results' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizPlayer;
